/**
 * Generic wrapper that pairs a React Query mutation with declarative
 * cache invalidation rules. Each domain (products, contacts, etc.)
 * defines a typed registry mapping mutation IDs to the queries they
 * should invalidate on success.
 *
 * Supports both:
 * - `invalidates`: standard query key invalidation
 * - `removes`: predicate-based query removal (for table keys that use
 *   dynamic patterns like `query.queryKey[0].includes('products-table')`)
 */

import {
  useMutation,
  useQueryClient,
  type QueryFilters,
  type QueryKey,
  type UseMutationOptions,
  type UseMutationResult,
} from '@tanstack/react-query'

export type InvalidationRule<TVariables = unknown> = {
  /** Query keys to invalidate when the mutation succeeds. */
  invalidates: (variables: TVariables) => QueryKey[]
  /** Predicate-based query removal (for dynamic/partial key matching). */
  removes?: (variables: TVariables) => QueryFilters[]
}

/**
 * A mutation hook that automatically invalidates/removes related queries
 * when the mutation succeeds.
 *
 * @param operationId - The mutation ID (must be a key in the registry)
 * @param registry - Typed mapping of mutation IDs to invalidation rules
 * @param options - Standard useMutation options (mutationFn, onSuccess, etc.)
 */
export function useInvalidatingMutation<
  TMutationId extends string,
  TData,
  TError,
  TVariables,
>(
  operationId: TMutationId,
  registry: Record<TMutationId, InvalidationRule<TVariables>>,
  options: UseMutationOptions<TData, TError, TVariables>
): UseMutationResult<TData, TError, TVariables> {
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      const rule = registry[operationId]

      // Invalidate listed query keys
      rule
        .invalidates(variables)
        .forEach((queryKey) => queryClient.invalidateQueries({ queryKey }))

      // Remove queries matching predicates (if any)
      rule.removes?.(variables).forEach((filters) => {
        queryClient.removeQueries(filters)
      })

      // Call through to any user-provided onSuccess
      options.onSuccess?.(data, variables, onMutateResult, context)
    },
  })
}

/**
 * Helper to create a predicate-based removal filter that matches
 * any query whose first key segment contains the given substring.
 * Useful for table keys like 'products-table', 'settings-products-table', etc.
 */
export function containsKeyFilter(substring: string): QueryFilters {
  return {
    predicate: (query) =>
      Array.isArray(query.queryKey) &&
      typeof query.queryKey[0] === 'string' &&
      query.queryKey[0].includes(substring),
  }
}
